import type { AlignmentData, EstimateConfig, HouseComposition, IcLocal3DStatus, IcProject, IcProjectListResponse, InspectionData, ManifestFetchResponse, Measurement, MeasurementType, Note, NoteCategory, Point3D, Project, S500ComplianceStatus, ScopeCatalog } from "../types.ts";
import { clearToken, getToken } from "../auth.ts";
import { ApiError, apiErrorFromResponse, reportNetworkError } from "./errors.ts";

export { ApiError };

const BASE = "/api";

/** 401 handling is shared: clear the token, notify the app, throw a
 *  consistent ApiError. */
function handleUnauthorized(path: string, method: string): ApiError {
  clearToken();
  window.dispatchEvent(new CustomEvent("auth:unauthorized"));
  return new ApiError({
    status: 401,
    code: "unauthorized",
    detail: "Unauthorized",
    path,
    method,
  });
}

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const method = (init?.method ?? "GET").toUpperCase();
  const token = getToken();
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string> ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try {
    return await fetch(BASE + path, { ...init, headers });
  } catch (err) {
    throw reportNetworkError(err, path, method);
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const res = await authedFetch(path, init);

  if (res.status === 401) throw handleUnauthorized(path, method);
  if (!res.ok) throw await apiErrorFromResponse(res, path, method);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// Fetches a binary resource with auth and returns a temporary blob URL.
// Caller is responsible for revoking the URL via URL.revokeObjectURL().
async function fetchMedia(path: string): Promise<string> {
  const res = await authedFetch(path);
  if (res.status === 401) throw handleUnauthorized(path, "GET");
  if (!res.ok) throw await apiErrorFromResponse(res, path, "GET");
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// In-memory cache: path → Promise<blobUrl>. Avoids re-downloading the same
// media when filters change or the lightbox is reopened. Blob URLs are kept
// alive for the lifetime of the page (never revoked by callers of this fn).
const _mediaCache = new Map<string, Promise<string>>();

// Cache for presigned URL strings (not blob URLs). The backend already caches
// for 23 h, but this avoids even a round-trip to our own backend on re-render.
const _presignedCache = new Map<string, Promise<string>>();

async function fetchPresignedUrl(path: string): Promise<string> {
  const res = await authedFetch(path);
  if (res.status === 401) throw handleUnauthorized(path, "GET");
  if (!res.ok) throw await apiErrorFromResponse(res, path, "GET");
  const j = await res.json() as { url?: string; error?: string };
  if (!j.url) throw new Error(j.error ?? "No URL in presigned-url response");
  return j.url;
}

function fetchPresignedUrlCached(path: string): Promise<string> {
  let p = _presignedCache.get(path);
  if (!p) {
    p = fetchPresignedUrl(path);
    p.catch(() => _presignedCache.delete(path));
    _presignedCache.set(path, p);
  }
  return p;
}

function fetchMediaCached(path: string): Promise<string> {
  let p = _mediaCache.get(path);
  if (!p) {
    p = fetchMedia(path);
    // On failure remove the entry so next call retries.
    p.catch(() => _mediaCache.delete(path));
    _mediaCache.set(path, p);
  }
  return p;
}

export type FetchProgress = (received: number, total: number | null) => void;

/** Same as fetchMediaCached but streams the response body so callers can
 *  display download progress. Total may be null if the server doesn't send
 *  Content-Length. Cached blobs short-circuit and skip onProgress entirely. */
function fetchMediaCachedWithProgress(
  path: string,
  onProgress?: FetchProgress,
): Promise<string> {
  const cached = _mediaCache.get(path);
  if (cached) return cached;
  const p = (async () => {
    const res = await authedFetch(path);
    if (res.status === 401) throw handleUnauthorized(path, "GET");
    if (!res.ok) throw await apiErrorFromResponse(res, path, "GET");
    const totalHeader = res.headers.get("Content-Length");
    const total = totalHeader ? parseInt(totalHeader, 10) : null;
    if (!res.body || !onProgress) {
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    }
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;
    onProgress(0, total);
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        received += value.byteLength;
        onProgress(received, total);
      }
    }
    const blob = new Blob(chunks as BlobPart[]);
    return URL.createObjectURL(blob);
  })();
  p.catch(() => _mediaCache.delete(path));
  _mediaCache.set(path, p);
  return p;
}

export interface LoginResponse {
  token: string;
  expiresAt: string;
  user: { id: number; username: string; displayName: string | null; qbUserId: string | null };
}

export const api = {
  auth: {
    login: async (
      username: string,
      password: string,
      deviceId: string,
    ): Promise<LoginResponse> => {
      const path = "/auth/login";
      let res: Response;
      try {
        res = await fetch(`${BASE}${path}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password, deviceId, deviceName: "inspect_3d Web", platform: "web" }),
        });
      } catch (err) {
        throw reportNetworkError(err, path, "POST");
      }
      if (!res.ok) throw await apiErrorFromResponse(res, path, "POST");
      return (await res.json()) as LoginResponse;
    },
    logout: (): Promise<void> =>
      req("/auth/logout", { method: "POST" }),
  },

  projects: {
    get: (id: string): Promise<Project> => req(`/projects/${id}`),
    assetUrl: (id: string, role: "glb" | "ply" | "usdz" | "floorplan" | "potree_manifest" | "rooms_data" | "roomplan_model") =>
      `${BASE}/projects/${id}/assets/${role}`,
    /** Auth-fetched blob URL for binary assets (glb/ply/floorplan/roomplan_model).
     *  Use instead of assetUrl when passing to loaders that cannot supply auth headers. */
    assetBlobUrl: (id: string, role: "glb" | "ply" | "usdz" | "floorplan" | "potree_manifest" | "rooms_data" | "roomplan_model"): Promise<string> =>
      fetchMediaCached(`/projects/${id}/assets/${role}`),
    /** Auth-fetched JSON for data assets (rooms_data). */
    assetJson: <T>(id: string, role: "rooms_data" | "potree_manifest"): Promise<T> =>
      req<T>(`/projects/${id}/assets/${role}`),
  },

  measurements: {
    list: (projectId: string): Promise<Measurement[]> => req(`/measurements/${projectId}`),
    create: (data: {
      project_id: string;
      type: MeasurementType;
      points: Point3D[];
      value?: number;
      unit?: string;
      label?: string;
    }): Promise<Measurement> =>
      req("/measurements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    delete: (id: string): Promise<void> => req(`/measurements/${id}`, { method: "DELETE" }),
  },

  notes: {
    list: (projectId: string): Promise<Note[]> => req(`/notes/${projectId}`),
    create: (data: {
      project_id: string;
      category: NoteCategory;
      text: string;
      point: Point3D;
    }): Promise<Note> =>
      req("/notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    delete: (id: string): Promise<void> => req(`/notes/${id}`, { method: "DELETE" }),
  },

  debug: {
    status: (): Promise<{ enabled: boolean }> => req("/debug/status"),
  },

  streetview: {
    manifestUrl: (projectId: string) => `${BASE}/projects/${projectId}/streetview/manifest`,
    diagnoseUrl: (projectId: string) => `${BASE}/projects/${projectId}/streetview/diagnose`,
    imageUrl: (projectId: string, imageId: number) =>
      `${BASE}/projects/${projectId}/streetview/images/${imageId}.jpg`,
  },

  icStreetview: {
    /** Returns a "?token=…" query suffix for streetview asset URLs that are
     *  loaded outside of fetch() (e.g. three.js TextureLoader). Empty when
     *  no token is stored — the backend will then 401. */
    tokenQuery: (): string => {
      const t = getToken();
      return t ? `?token=${encodeURIComponent(t)}` : "";
    },
    manifestUrl: (icProjectId: string) => {
      const t = getToken();
      const q = t ? `?token=${encodeURIComponent(t)}` : "";
      return `${BASE}/ic/projects/${encodeURIComponent(icProjectId)}/streetview/manifest${q}`;
    },
    /** Manifest URL for a specific capture ZIP (per-room streetview). */
    manifestUrlForCapture: (icProjectId: string, zipFilename: string) => {
      const t = getToken();
      const q = t
        ? `?token=${encodeURIComponent(t)}&filename=${encodeURIComponent(zipFilename)}`
        : `?filename=${encodeURIComponent(zipFilename)}`;
      return `${BASE}/ic/projects/${encodeURIComponent(icProjectId)}/streetview/manifest${q}`;
    },
    /** imageUrlQuery suffix for per-capture images (appended to each {nodeId}.jpg). */
    imageUrlQueryForCapture: (zipFilename: string) => {
      const t = getToken();
      return t
        ? `?token=${encodeURIComponent(t)}&filename=${encodeURIComponent(zipFilename)}`
        : `?filename=${encodeURIComponent(zipFilename)}`;
    },
    diagnoseUrl: (icProjectId: string) => {
      const t = getToken();
      const q = t ? `?token=${encodeURIComponent(t)}` : "";
      return `${BASE}/ic/projects/${encodeURIComponent(icProjectId)}/streetview/diagnose${q}`;
    },
    imageBaseUrl: (icProjectId: string) =>
      `${BASE}/ic/projects/${encodeURIComponent(icProjectId)}/streetview/images`,
  },

  pano360: {
    manifestUrl: (projectId: string) => `${BASE}/projects/${projectId}/pano360/manifest`,
    imageBaseUrl: (projectId: string) => `${BASE}/projects/${projectId}/pano360/images`,
    align: (
      projectId: string,
      nodeId: number,
      body: { position: { x: number; y: number; z: number }; yaw: number; aligned: boolean },
    ): Promise<{ status: string; node_id: number }> =>
      req(`/projects/${projectId}/pano360/align/${nodeId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  },

  inspection: {
    load: (projectId: string): Promise<InspectionData> => req(`/projects/${projectId}/inspection`),
    mediaUrl: (projectId: string, filename: string) =>
      `${BASE}/projects/${projectId}/inspection/media/${encodeURIComponent(filename)}`,
  },

  alignment: {
    get: async (projectId: string): Promise<AlignmentData | null> => {
      const path = `/projects/${projectId}/alignment`;
      const res = await authedFetch(path);
      if (res.status === 401) throw handleUnauthorized(path, "GET");
      if (res.status === 204) return null;
      if (!res.ok) throw await apiErrorFromResponse(res, path, "GET");
      return res.json();
    },
    save: (projectId: string, data: AlignmentData): Promise<AlignmentData> =>
      req(`/projects/${projectId}/alignment`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    delete: (projectId: string): Promise<void> =>
      req(`/projects/${projectId}/alignment`, { method: "DELETE" }),
  },

  ic: {
    health: (): Promise<{ configured: boolean; base_url: string | null }> =>
      req("/ic/health"),
    listProjects: (since = 0): Promise<IcProjectListResponse> =>
      req(`/ic/projects?since=${since}`),
    getProject: (icProjectId: string): Promise<{ project: IcProject }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}`),
    getManifest: (icProjectId: string): Promise<ManifestFetchResponse> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/manifest`),
    getS500Status: (icProjectId: string): Promise<S500ComplianceStatus> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/s500-status`),
    getLocal3D: (icProjectId: string): Promise<IcLocal3DStatus> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/local-3d`),
    generateFloorplan: async (icProjectId: string): Promise<string> => {
      const path = `/ic/projects/${encodeURIComponent(icProjectId)}/generate-floorplan`;
      const res = await authedFetch(path, { method: "POST" });
      if (res.status === 401) throw handleUnauthorized(path, "POST");
      if (!res.ok) throw await apiErrorFromResponse(res, path, "POST");
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    captureMediaUrl: (icProjectId: string, captureId: string): string =>
      `${BASE}/ic/projects/${encodeURIComponent(icProjectId)}/captures/${encodeURIComponent(captureId)}/media`,
    captureMediaBlobUrl: (icProjectId: string, captureId: string): Promise<string> =>
      fetchMediaCached(`/ic/projects/${encodeURIComponent(icProjectId)}/captures/${encodeURIComponent(captureId)}/media`),
    /** Fetch a capture's file and decode it as a UTF-8 text string. Used for
     *  DXF files to avoid potential encoding ambiguity when going through a
     *  blob URL intermediary. */
    captureMediaText: async (icProjectId: string, captureId: string): Promise<string> => {
      const path = `/ic/projects/${encodeURIComponent(icProjectId)}/captures/${encodeURIComponent(captureId)}/media`;
      const res = await authedFetch(path);
      if (res.status === 401) throw handleUnauthorized(path, "GET");
      if (!res.ok) throw await apiErrorFromResponse(res, path, "GET");
      return res.text();
    },
    /** Same as captureMediaBlobUrl but asks IC for the H.264 sidecar copy of
     *  HEVC video sources so Firefox/Chrome can decode. IC falls back to the
     *  original when no sidecar exists (e.g. before the worker has processed
     *  it, or for non-video captures). */
    captureMediaWebBlobUrl: (icProjectId: string, captureId: string): Promise<string> =>
      fetchMediaCached(`/ic/projects/${encodeURIComponent(icProjectId)}/captures/${encodeURIComponent(captureId)}/media?profile=web`),
    /** ~400 px JPEG preview for image captures. Backend generates and caches
     *  on first request; subsequent calls hit a disk cache. Non-image
     *  captures transparently fall back to the original stream. */
    captureMediaThumbBlobUrl: (icProjectId: string, captureId: string): Promise<string> =>
      fetchMediaCached(`/ic/projects/${encodeURIComponent(icProjectId)}/captures/${encodeURIComponent(captureId)}/media?size=thumb`),
    /** ~1600 px JPEG for center-pane display. Disk-cached after first request. */
    captureMediaMediumBlobUrl: (icProjectId: string, captureId: string): Promise<string> =>
      fetchMediaCached(`/ic/projects/${encodeURIComponent(icProjectId)}/captures/${encodeURIComponent(captureId)}/media?size=medium`),
    /** Pre-signed direct R2 URL — browser fetches the file without any proxy.
     *  Returns the URL string (not a blob URL). Rejects with an error if the
     *  storage backend doesn't support pre-signed URLs (e.g. Google Drive). */
    capturePresignedUrl: (icProjectId: string, captureId: string): Promise<string> =>
      fetchPresignedUrlCached(`/ic/projects/${encodeURIComponent(icProjectId)}/captures/${encodeURIComponent(captureId)}/presigned-url`),
    /** Direct IC asset URL — bypasses the local project asset route.
     *  Roles: glb, ply, floorplan, rooms_data, roomplan_model */
    assetUrl: (icProjectId: string, role: string): string =>
      `${BASE}/ic/projects/${encodeURIComponent(icProjectId)}/assets/${encodeURIComponent(role)}`,
    /** Direct project-root scan-file URL (e.g. per-room GLB).
     *  Note: the endpoint requires a bearer token. Loaders that can't supply
     *  custom headers (e.g. three.js GLTFLoader) should use `scanFileBlobUrl`. */
    scanFileUrl: (icProjectId: string, filename: string): string =>
      `${BASE}/ic/projects/${encodeURIComponent(icProjectId)}/scan-files/${encodeURIComponent(filename)}`,
    /** Auth-fetched blob URL for a project-root scan-file. */
    scanFileBlobUrl: (icProjectId: string, filename: string): Promise<string> =>
      fetchMediaCached(`/ic/projects/${encodeURIComponent(icProjectId)}/scan-files/${encodeURIComponent(filename)}`),
    /** Same as scanFileBlobUrl but reports streaming download progress. */
    scanFileBlobUrlWithProgress: (
      icProjectId: string,
      filename: string,
      onProgress?: FetchProgress,
    ): Promise<string> =>
      fetchMediaCachedWithProgress(
        `/ic/projects/${encodeURIComponent(icProjectId)}/scan-files/${encodeURIComponent(filename)}`,
        onProgress,
      ),
    /** Fire-and-forget: pre-generate thumbs for the given uploaded photo captures. */
    warmThumbs: (icProjectId: string, captureIds: string[]): Promise<unknown> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/warm-thumbs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ capture_ids: captureIds }),
      }),
    uploadCapture: async (
      icProjectId: string,
      opts: {
        file: File;
        inspectionId: string;
        phase: string;
        mediaType: string;
        intent?: string;
        roomId?: string;
        roomName?: string;
      },
    ): Promise<{ capture_id: string }> => {
      const path = `/ic/projects/${encodeURIComponent(icProjectId)}/captures`;
      const fd = new FormData();
      fd.append("file", opts.file);
      fd.append("inspection_id", opts.inspectionId);
      fd.append("phase", opts.phase);
      fd.append("media_type", opts.mediaType);
      if (opts.intent) fd.append("intent", opts.intent);
      if (opts.roomId) fd.append("room_id", opts.roomId);
      if (opts.roomName) fd.append("room_name", opts.roomName);
      const res = await authedFetch(path, { method: "POST", body: fd });
      if (res.status === 401) throw handleUnauthorized(path, "POST");
      if (!res.ok) throw await apiErrorFromResponse(res, path, "POST");
      return res.json();
    },
    downloadPhotosZip: async (icProjectId: string): Promise<string> => {
      const path = `/ic/projects/${encodeURIComponent(icProjectId)}/photos.zip`;
      const res = await authedFetch(path);
      if (res.status === 401) throw handleUnauthorized(path, "GET");
      if (!res.ok) throw await apiErrorFromResponse(res, path, "GET");
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    s500PacketUrl: (icProjectId: string): string =>
      `${BASE}/ic/projects/${encodeURIComponent(icProjectId)}/s500-packet.pdf`,
    autoGenerateEstimate: (icProjectId: string): Promise<{ items: unknown[]; manifest_version: number }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/scope/auto-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      }),
    addScopeItems: (
      icProjectId: string,
      items: Array<{ description: string; code?: string | null; quantity?: number | null; unit?: string | null; unit_price?: number | null; room_id?: string | null; room_name?: string | null; source?: string }>,
      clientOpId: string,
    ): Promise<{ items: unknown[]; manifest_version: number }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/scope-items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_op_id: clientOpId, items }),
      }),
    patchScopeItem: (
      icProjectId: string,
      scopeId: string,
      patch: { description?: string; code?: string; quantity?: number; unit?: string; unit_price?: number; rejected?: boolean },
    ): Promise<{ manifest_version: number }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/scope/${encodeURIComponent(scopeId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }),
    deleteScopeItem: (
      icProjectId: string,
      scopeId: string,
    ): Promise<{ manifest_version: number }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/scope/${encodeURIComponent(scopeId)}`, {
        method: "DELETE",
      }),
    patchEstimateConfig: (
      icProjectId: string,
      config: EstimateConfig,
    ): Promise<{ manifest_version: number }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/estimate-config`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(config),
      }),
    getScopeCatalog: (() => {
      let _cache: ScopeCatalog | null = null;
      return async (): Promise<ScopeCatalog> => {
        if (_cache) return _cache;
        _cache = await req<ScopeCatalog>("/ic/config/scope-catalog");
        return _cache;
      };
    })(),
    downloadEstimateEsx: async (icProjectId: string): Promise<string> => {
      const path = `/ic/projects/${encodeURIComponent(icProjectId)}/estimate.esx`;
      const res = await authedFetch(path);
      if (res.status === 401) throw handleUnauthorized(path, "GET");
      if (!res.ok) throw await apiErrorFromResponse(res, path, "GET");
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    downloadEstimatePdf: async (icProjectId: string): Promise<string> => {
      const path = `/ic/projects/${encodeURIComponent(icProjectId)}/estimate.pdf`;
      const res = await authedFetch(path);
      if (res.status === 401) throw handleUnauthorized(path, "GET");
      if (!res.ok) throw await apiErrorFromResponse(res, path, "GET");
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    getHouseComposition: (icProjectId: string): Promise<HouseComposition> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/house-composition`),
    getArUcoAnchors: (icProjectId: string): Promise<{
      markers: Array<{ marker_id: number; world_xyz: [number, number, number] | null; camera_pose: number[]; marker_in_camera: number[]; marker_size_m: number; scan_capture_id: string | null }>;
      anchored_photos: Array<{ capture_id: string; drive_file_id: string | null; filename: string; marker_id: number; world_xyz: [number, number, number] | null; phase: string; room_id: string | null; captured_at: string | null }>;
    }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/aruco-anchors`),
    putHouseComposition: (icProjectId: string, composition: HouseComposition): Promise<HouseComposition> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/house-composition`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(composition),
      }),
    generateClientLink: (icProjectId: string): Promise<{ token: string; url: string; expires_in_days: number }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/client-link`, { method: "POST" }),
    patchInsurance: (
      icProjectId: string,
      fields: {
        policy_number?: string | null;
        insurance_carrier?: string | null;
        deductible?: number | null;
        rcv?: number | null;
        acv?: number | null;
        expected_state_version: number;
        client_op_id?: string;
      },
    ): Promise<unknown> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/assignment/insurance`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      }),
    triggerCaptureGenerate: (
      icProjectId: string,
      captureId: string,
      jobType: "glb" | "floorplan" | "transcript",
    ): Promise<{ job_type: string; job_id: number | null; status: string }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/captures/${encodeURIComponent(captureId)}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_type: jobType }),
      }),
    getCaptureJobs: (icProjectId: string): Promise<{ jobs: import("../types").CaptureJobStatus[] }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/capture-jobs`),
    deleteCapture: (icProjectId: string, captureId: string): Promise<{ deleted: boolean; capture_id: string; file_deleted: boolean }> =>
      req(`/ic/projects/${encodeURIComponent(icProjectId)}/captures/${encodeURIComponent(captureId)}`, {
        method: "DELETE",
      }),
  },

};
